import { IssueSourceType, IssueState } from '@/types';
import {
    SET_TRIAGE_DESTINATIONS,
    SET_TRIAGE_OWNERS,
    SET_TRIAGE_RELEASE_FILTER,
    SET_TRIAGE_REPORTERS,
    SET_TRIAGE_SEARCH,
    SET_TRIAGE_SORT_BY,
    SET_TRIAGE_SOURCES,
    SET_TRIAGE_STATUS_GROUP,
    SET_TRIAGE_STATUSES,
    SET_TRIAGE_VERSIONS,
    TriageFiltersAction,
    TriageReleaseFilter,
    TriageSortBy,
    TriageStatusGroup,
} from '@/actions/triage';

export interface TriageFiltersState {
    statusGroup: TriageStatusGroup;
    statuses: IssueState[];
    sources: IssueSourceType[];
    owners: string[];
    reporters: string[];
    versions: string[];
    destinations: string[];
    sortBy: TriageSortBy;
    releaseFilter: TriageReleaseFilter;
    search: string;
}

const defaultFilters: TriageFiltersState = {
    statusGroup: 'open',
    statuses: [],
    sources: [],
    owners: [],
    reporters: [],
    versions: [],
    destinations: [],
    sortBy: 'triage',
    releaseFilter: 'none',
    search: '',
};

// Keyed by appId so each app keeps its own filter state.
export type TriageFiltersByAppId = Record<string, TriageFiltersState>;

const filtersForApp = (state: TriageFiltersByAppId, appId: string): TriageFiltersState =>
    state[appId] ?? defaultFilters;

export function triageFiltersReducer(
    state: TriageFiltersByAppId = {},
    action: TriageFiltersAction,
): TriageFiltersByAppId {
    switch (action.type) {
        case SET_TRIAGE_STATUS_GROUP: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    statusGroup: action.payload.statusGroup,
                },
            };
        }
        case SET_TRIAGE_STATUSES: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    statuses: action.payload.statuses,
                },
            };
        }
        case SET_TRIAGE_SOURCES: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    sources: action.payload.sources,
                },
            };
        }
        case SET_TRIAGE_OWNERS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    owners: action.payload.owners,
                },
            };
        }
        case SET_TRIAGE_REPORTERS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    reporters: action.payload.reporters,
                },
            };
        }
        case SET_TRIAGE_VERSIONS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    versions: action.payload.versions,
                },
            };
        }
        case SET_TRIAGE_DESTINATIONS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    destinations: action.payload.destinations,
                },
            };
        }
        case SET_TRIAGE_SORT_BY: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    sortBy: action.payload.sortBy,
                },
            };
        }
        case SET_TRIAGE_RELEASE_FILTER: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    releaseFilter: action.payload.releaseFilter,
                },
            };
        }
        case SET_TRIAGE_SEARCH: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    search: action.payload.search,
                },
            };
        }
        default:
            return state;
    }
}

export { defaultFilters as DEFAULT_TRIAGE_FILTERS };
export default triageFiltersReducer;
